import { useCallback, useEffect, useReducer, useRef, useState } from 'react';

import { LTLQuickQuoteRequest } from 'lib/api/getLTLQuickQuotes';
import { Maybe } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';
import captureException from 'utils/captureException';
import { mapWithConcurrency } from 'utils/mapWithConcurrency';

import { useLTLQuoteJob } from '../../LTL/hooks/useLTLQuoteJob';
import { LTL_LEGACY_SYSTEM_FAILURE_MESSAGE } from '../../ltlProviderOutcomeConfig';
import { QuickQuoteInputs } from '../../types';
import { getLTLQuoteIdentity } from '../../util';
import { isRequestAborted } from '../../utils/abortableWait';
import {
  EMPTY_LTL_EXTERNAL_QUOTE,
  getLTLExternalQuote,
} from '../../utils/classifyLTLQuoteResult';
import {
  LTLQuoteJobOutcome,
  LTLQuoteJobPointer,
  LTLQuoteJobStatus,
} from '../../utils/runLTLQuoteJob';
import {
  BATCH_LTL_EMPTY_QUOTES_MESSAGE,
  BATCH_LTL_RATE_CONCURRENCY,
} from '../constants';
import {
  BatchLTLPersistedRun,
  BatchLTLRunsByKey,
  BatchLTLShipmentRun,
  BatchLTLShipmentStatus,
} from '../types';

export type BatchLTLRateRequest = {
  key: number;
  // Normalized form values; the Shiplify check runs on these before the request is built.
  formValues: QuickQuoteInputs;
};

type UseBatchLTLRatingParams = {
  savedRuns?: Record<number, BatchLTLPersistedRun>;
  buildRequest: (
    key: number,
    formValues: QuickQuoteInputs
  ) => LTLQuickQuoteRequest;
  // Shiplify filled in lookups or recommended accessorials for this shipment.
  onShiplifyChecked?: (key: number, formValues: QuickQuoteInputs) => void;
  // The server resolved (or created, for an added shipment) the shipment's quote request.
  onQuoteRequestResolved?: (key: number, quoteRequestId: number) => void;
};

type RunAction =
  | { type: 'started'; key: number; run?: BatchLTLShipmentRun }
  | { type: 'finished'; key: number; run: BatchLTLShipmentRun }
  | { type: 'removed'; key: number };

type ResumableRun = BatchLTLPersistedRun & { jobId: string };

const RATING_RUN: BatchLTLShipmentRun = {
  status: BatchLTLShipmentStatus.Rating,
  quotes: [],
  defaultSelectedQuoteIds: [],
  externalQuote: EMPTY_LTL_EXTERNAL_QUOTE,
  failureMessage: null,
  jobId: null,
  startedAt: null,
  stops: [],
};

// Rates each shipment through the same LTL job hook as the single Quick Quote form
// (Shiplify check, start, poll, classify), a few at a time. Results land per shipment as
// each job finishes. Saved runs resume from their job ids: the server keeps job results,
// so the browser only needs the pointer.
export function useBatchLTLRating({
  savedRuns = {},
  buildRequest,
  onShiplifyChecked,
  onQuoteRequestResolved,
}: UseBatchLTLRatingParams) {
  const { runQuote, resumeQuote } = useLTLQuoteJob({ tms: TMS.Tai });
  // Captured once: later saves of the same runs must not restart them.
  const [initialRuns] = useState(() => savedRuns);
  const [runsByKey, dispatch] = useReducer(
    runsReducer,
    initialRuns,
    toInitialRuns
  );
  // One controller per shipment so a retry cancels only that shipment's poll.
  const controllersRef = useRef(new Map<number, AbortController>());
  // The panel's latest callbacks, read when a job reaches each step.
  const callbacksRef = useRef({
    buildRequest,
    onShiplifyChecked,
    onQuoteRequestResolved,
  });
  useEffect(() => {
    callbacksRef.current = {
      buildRequest,
      onShiplifyChecked,
      onQuoteRequestResolved,
    };
  }, [buildRequest, onShiplifyChecked, onQuoteRequestResolved]);

  // Stop polling when the panel unmounts (rep left the thread).
  useEffect(() => {
    const controllers = controllersRef.current;
    return () => {
      controllers.forEach((controller) => controller.abort());
      controllers.clear();
    };
  }, []);

  // Runs a shipment's job under its own controller and records the outcome.
  const trackShipment = useCallback(
    async (
      key: number,
      run: (signal: AbortSignal) => Promise<LTLQuoteJobOutcome>
    ) => {
      controllersRef.current.get(key)?.abort();
      const controller = new AbortController();
      controllersRef.current.set(key, controller);

      try {
        const outcome = await run(controller.signal);
        if (controller.signal.aborted) {
          return;
        }

        const result = toShipmentRun(outcome);
        dispatch(
          result
            ? { type: 'finished', key, run: result }
            : { type: 'removed', key }
        );
      } catch (error) {
        if (isRequestAborted(error)) {
          return;
        }

        captureException(error, { functionName: 'useBatchLTLRating' });
        dispatch({
          type: 'finished',
          key,
          run: toFailedRun(LTL_LEGACY_SYSTEM_FAILURE_MESSAGE),
        });
      } finally {
        if (controllersRef.current.get(key) === controller) {
          controllersRef.current.delete(key);
        }
      }
    },
    []
  );

  const rateShipment = useCallback(
    ({ key, formValues }: BatchLTLRateRequest) =>
      trackShipment(key, (signal) => {
        dispatch({ type: 'started', key });

        return runQuote({
          formValues,
          buildRequest: (checkedValues) =>
            callbacksRef.current.buildRequest(key, checkedValues),
          signal,
          onShiplifyChecked: (checkedValues) =>
            callbacksRef.current.onShiplifyChecked?.(key, checkedValues),
          // The job id is saved right away so leaving mid-rate does not lose it.
          onJobStarted: (pointer: LTLQuoteJobPointer, quoteRequestId) => {
            dispatch({
              type: 'started',
              key,
              run: { ...RATING_RUN, ...pointer },
            });
            callbacksRef.current.onQuoteRequestResolved?.(key, quoteRequestId);
          },
        });
      }),
    [runQuote, trackShipment]
  );

  const rateShipments = useCallback(
    async (requests: BatchLTLRateRequest[]) => {
      requests.forEach(({ key }) => dispatch({ type: 'started', key }));
      await mapWithConcurrency(
        requests,
        BATCH_LTL_RATE_CONCURRENCY,
        rateShipment
      );
    },
    [rateShipment]
  );

  // Picks up saved jobs once per mount: still-rating jobs are polled, finished ones are
  // re-read once to load their quotes, and jobs the server no longer has are dropped.
  useEffect(() => {
    const resumable = Object.entries(initialRuns)
      .filter((entry): entry is [string, ResumableRun] =>
        Boolean(entry[1].jobId)
      )
      .map(([key, run]) => ({ key: Number(key), run }));

    void mapWithConcurrency(
      resumable,
      BATCH_LTL_RATE_CONCURRENCY,
      ({ key, run }) =>
        trackShipment(key, (signal) =>
          resumeQuote(
            {
              jobId: run.jobId,
              startedAt: run.startedAt ?? 0,
              stops: run.stops,
            },
            signal
          )
        )
    );
  }, [initialRuns, resumeQuote, trackShipment]);

  const isRating = Object.values(runsByKey).some(
    (run) => run.status === BatchLTLShipmentStatus.Rating
  );

  const invalidateShipment = useCallback((key: number) => {
    controllersRef.current.get(key)?.abort();
    controllersRef.current.delete(key);
    dispatch({ type: 'removed', key });
  }, []);

  return { runsByKey, rateShipments, invalidateShipment, isRating };
}

function runsReducer(
  state: BatchLTLRunsByKey,
  action: RunAction
): BatchLTLRunsByKey {
  switch (action.type) {
    case 'started':
      return { ...state, [action.key]: action.run ?? RATING_RUN };
    case 'finished':
      return { ...state, [action.key]: action.run };
    case 'removed': {
      const { [action.key]: _removed, ...rest } = state;
      return rest;
    }
    default:
      return state;
  }
}

// Saved runs with a job show as rating until the server answers; a start that failed
// before a job existed shows its saved message.
function toInitialRuns(
  savedRuns: Record<number, BatchLTLPersistedRun>
): BatchLTLRunsByKey {
  return Object.fromEntries(
    Object.entries(savedRuns).map(([key, run]) => [
      key,
      run.jobId
        ? {
            ...RATING_RUN,
            jobId: run.jobId,
            startedAt: run.startedAt,
            stops: run.stops,
          }
        : toFailedRun(run.failureMessage ?? LTL_LEGACY_SYSTEM_FAILURE_MESSAGE),
    ])
  );
}

// A shipment with quotes but none worth sending (all errored or not serviced) is a failed
// run that still shows its carriers. Null when the server no longer knows a resumed job.
function toShipmentRun(
  outcome: LTLQuoteJobOutcome
): Maybe<BatchLTLShipmentRun> {
  if (outcome.status === LTLQuoteJobStatus.Unknown) {
    return null;
  }

  if (outcome.status === LTLQuoteJobStatus.Failed) {
    return {
      ...toFailedRun(outcome.failureMessage),
      ...(outcome.pointer ?? {}),
    };
  }

  const { result, jobId, startedAt, stops } = outcome;
  const hasRates = result.defaultSelectedQuotes.length > 0;

  return {
    status: hasRates
      ? BatchLTLShipmentStatus.Rated
      : BatchLTLShipmentStatus.Failed,
    quotes: result.quotes,
    defaultSelectedQuoteIds:
      result.defaultSelectedQuotes.map(getLTLQuoteIdentity),
    externalQuote: getLTLExternalQuote(result),
    failureMessage: hasRates
      ? null
      : (result.providerFailureMessage ?? BATCH_LTL_EMPTY_QUOTES_MESSAGE),
    jobId,
    startedAt,
    stops,
  };
}

function toFailedRun(failureMessage: string): BatchLTLShipmentRun {
  return {
    ...RATING_RUN,
    status: BatchLTLShipmentStatus.Failed,
    failureMessage,
  };
}
